import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Combobox, ComboboxOption } from '@leafygreen-ui/combobox';
import Icon from '@leafygreen-ui/icon';
import { palette } from '@leafygreen-ui/palette';

// services
import { organization } from 'js/common/services/api';

// utils
import { CloudTeams, sendError } from 'js/common/utils/bugsnag';

// types
import { VercelProject } from 'js/common/types/vercel';
export interface VercelProjectView extends VercelProject {
  isLinked: boolean;
}

// styles
import { VercelDescription, VercelBodyLabel } from 'js/common/components/Vercel/styles/common';

const LinkerContainer = styled.div`
  margin-top: 24px;
`;

const ComboboxContainer = styled.div`
  display: flex;
  margin-top: 16px;

  label {
    color: ${palette.gray.dark3};
    line-height: 20px;
  }

  div[role='combobox'] {
    min-width: 141px;
    width: 216px;
    input {
      width: auto;
    }
  }

  div[data-leafygreen-ui='interaction-ring'] {
    right: 24px;
  }
`;

const ArrowContainer = styled.div`
  display: flex;
  margin: 32px 12px;
`;

export default function VercelProjectsToClusterLinker({
  orgId = '',
  centralUrl = '',
  clusterNames = [],
  defaultClusterName = '',
  isClusterDisabled = false,
  onChangeCluster = () => {},
  onChangeVercelProjects = () => {},
  defaultVercelProjectsIds = [],
}: {
  orgId?: string;
  centralUrl?: string;
  clusterNames: Array<string>;
  defaultClusterName?: string;
  isClusterDisabled?: boolean;
  onChangeCluster?: (string) => void;
  onChangeVercelProjects?: (projects: Array<VercelProject>) => void;
  defaultVercelProjectsIds?: Array<string>;
}) {
  const [vercelProjects, setVercelProjects] = useState<Array<VercelProjectView>>([]);
  const [selectedCluster, setSelectedCluster] = useState('');
  const [selectedVercelProjectsIds, setSelectedVercelProjectsIds] = useState<Array<string>>([]);

  useEffect(() => {
    async function getVercelProjects() {
      try {
        const projects: Array<VercelProjectView> = await organization.getVercelProjects({ orgId, centralUrl });
        setVercelProjects(projects);
      } catch (error) {
        sendError({ error, team: CloudTeams.CoreIam });
      }
    }

    if (orgId !== '') {
      getVercelProjects();
    }
  }, [orgId]);

  const onChangeSelectedProjects = (selectedVercelProjectsIds: Array<string>) => {
    let projectsWithIdsAndNames: Array<VercelProject> = [];

    vercelProjects.forEach((vercelProject) => {
      if (selectedVercelProjectsIds.includes(vercelProject.id)) {
        let projectWithoutIsLinked;

        // remove 'isLinked' as it's not actual part of VercelProject object
        projectWithoutIsLinked = Object.assign({}, vercelProject);
        delete projectWithoutIsLinked.isLinked;
        projectsWithIdsAndNames.push(projectWithoutIsLinked);
      }
    });

    setSelectedVercelProjectsIds(selectedVercelProjectsIds);
    onChangeVercelProjects(projectsWithIdsAndNames);
  };

  return (
    <LinkerContainer>
      <VercelBodyLabel>Link Clusters and Projects</VercelBodyLabel>
      <VercelDescription>
        Select the Atlas Clusters and Vercel Projects to link. You may add any additional mappings later on the MongoDB
        UI.
      </VercelDescription>
      <ComboboxContainer>
        <Combobox
          label="Atlas Cluster"
          placeholder="Select a cluster"
          clearable={false}
          disabled={isClusterDisabled}
          value={defaultClusterName || selectedCluster}
          onChange={(cluster) => {
            setSelectedCluster(cluster);
            onChangeCluster(cluster);
          }}
        >
          {clusterNames.length > 0 &&
            clusterNames.map((clusterName) => <ComboboxOption value={clusterName} displayName={clusterName} />)}
        </Combobox>
        <ArrowContainer>
          <Icon glyph="ArrowLeft" />
          <Icon glyph="ArrowRight" />
        </ArrowContainer>
        <Combobox
          label="Vercel Projects"
          multiselect
          placeholder="Select projects"
          onChange={onChangeSelectedProjects}
          value={defaultVercelProjectsIds.length > 0 ? defaultVercelProjectsIds : selectedVercelProjectsIds}
          filteredOptions={vercelProjects.map((project) => (!project.isLinked ? project.id : ''))}
          chipTruncationLocation="end"
        >
          {vercelProjects.length > 0 &&
            vercelProjects.map(({ id, name, isLinked }) => (
              <ComboboxOption value={id} displayName={isLinked ? `${name} (Already Linked)` : name} />
            ))}
        </Combobox>
      </ComboboxContainer>
    </LinkerContainer>
  );
}
